import isDebug from 'utils/isDebug'

import {
  get,
  ApiResponse,
} from 'utils/apis/insuranceLounge'

import {
  CacheResponse,
  GenericAction,
} from 'state/types'

import {
  ActionTypes,
  CoverageLevel,
  CoverageLevelsByState,
} from './types'

const {
  CACHE_BEGIN,
  CACHE_SUCCESS,
  CACHE_FAILURE,
  SET_COVERAGE_LEVELS,
} = ActionTypes

const fetchCoverageLevels = async (insuranceType?: string, stateAbbr?: string): Promise<CoverageLevelsByState> => {
  const url = (!insuranceType && '/coverage-levels') || (!stateAbbr && `/coverage-levels/${insuranceType}`) || `/coverage-levels/${insuranceType}/${stateAbbr}`

  const coverageLevels: ApiResponse = await get(url)

  if (!coverageLevels.success) {
    throw coverageLevels
  }

  return coverageLevels.data as CoverageLevelsByState
}

// eslint-disable-next-line import/prefer-default-export
export const cacheCoverageLevels = (insuranceType: string, stateAbbr?: string) => () => async (dispatch: Function, getState: Function): CacheResponse => {
  if (getState().coverageLevels.cached.insuranceTypes[insuranceType] || (stateAbbr && getState().coverageLevels.cached.insuranceTypeDotStates[`${insuranceType}.${stateAbbr}`])) {
    if (isDebug) {
      console.info(`[DEBUG] Coverage Levels already cached for ${insuranceType}${(stateAbbr && `.${stateAbbr}`) || ''}, will not re-cache.`) // eslint-disable-line no-console
    }

    return true
  }

  if (getState().coverageLevels.loading.insuranceTypes[insuranceType] || (stateAbbr && getState().coverageLevels.loading.insuranceTypeDotStates[`${insuranceType}.${stateAbbr}`])) {
    if (isDebug) {
      console.info(`[DEBUG] Coverage Levels currently loading for ${insuranceType}${(stateAbbr && `.${stateAbbr}`) || ''}, will not make second fetch request.`) // eslint-disable-line no-console
    }

    return true
  }

  dispatch(cacheCoverageLevelsBegin(insuranceType, stateAbbr))

  return await fetchCoverageLevels(insuranceType, stateAbbr)
    .then((coverageLevels: CoverageLevelsByState) => {
      if (!coverageLevels) {
        const error = 'No coverage levels returned.'

        dispatch(cacheCoverageLevelsFailure(error))

        throw new Error(error)
      }

      dispatch(cacheCoverageLevelsSuccess(coverageLevels, insuranceType, stateAbbr))
    })
    .catch(error => {
      dispatch(cacheCoverageLevelsFailure(error, insuranceType, stateAbbr))

      return Promise.reject(error.message || 'Unable to cache coverage levels.')
    })
}

export const setCoverageLevels = (insuranceType?: string, stateAbbr?: string, coverageLevels?: CoverageLevel[]) => (dispatch: Function): GenericAction => {
  return dispatch({
    type: SET_COVERAGE_LEVELS,
    payload: {
      coverageLevels,
      insuranceType,
      stateAbbr,
    },
  })
}

const cacheCoverageLevelsBegin = (insuranceType?: string, stateAbbr?: string): GenericAction => ({
  type: CACHE_BEGIN,
  payload: {
    insuranceType,
    stateAbbr,
  },
})

const cacheCoverageLevelsSuccess = (coverageLevels: CoverageLevelsByState, insuranceType?: string, stateAbbr?: string): GenericAction => ({
  type: CACHE_SUCCESS,
  payload: {
    coverageLevels,
    insuranceType,
    stateAbbr,
  },
})

const cacheCoverageLevelsFailure = (error: string, insuranceType?: string, stateAbbr?: string): GenericAction => ({
  type: CACHE_FAILURE,
  payload: {
    error,
    insuranceType,
    stateAbbr,
  },
})
